'use client';

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertCircle, Phone, Loader2, Save } from 'lucide-react';
import axios from 'axios';
import { useAppAuth } from '@/hooks/useAppAuth';
import dynamic from 'next/dynamic';
const PhoneInput = dynamic(() => import('react-phone-number-input'), { ssr: false });
import 'react-phone-number-input/style.css';
import flags from 'react-phone-number-input/flags';
import { API_URL } from '@/lib/api-config';

export default function MobileNumberModal({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose?: () => void;
}) {
  const { refreshSession, getToken } = useAppAuth();
  const [phone, setPhone] = useState<string>('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSave = async () => {
    if (!phone) {
      setError('Please enter a valid phone number');
      return;
    }

    if (isSaving) return; // Prevent double-click

    setIsSaving(true);
    setError('');

    try {
      const token = await getToken();

      const response = await axios.put(
        `${API_URL}/public/user/firebase-profile`,
        { mobile: phone },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      // Allow Firebase and DB to sync slightly
      setTimeout(async () => {
        await refreshSession();
        setIsSaving(false);
        // Close modal after successful save (parent will detect hasMobile === true)
        if (onClose) {
          onClose();
        }
      }, 1500);
    } catch (err: any) {
      if (err?.response?.status === 401) {
        setError(
          'Session unauthorized. Please refresh the page or log in again.',
        );
      } else if (err.message?.includes('No valid authentication token')) {
        setError('Your session has expired. Please log in again.');
      } else {
        setError(
          err?.response?.data?.message ||
            err?.message ||
            'Failed to update phone number',
        );
      }
      setIsSaving(false);
    }
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
        />
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 10 }}
          className="bg-white rounded-[24px] shadow-2xl w-full max-w-md relative z-10 overflow-hidden border-[4px] border-white drop-shadow-sm font-display"
        >
          <div className="bg-slate-50 px-8 py-6 flex items-center justify-center border-b border-slate-100 flex-col text-center">
            <div className="w-16 h-16 bg-amber-100 text-amber-500 rounded-full flex items-center justify-center mb-4 border border-amber-200 shadow-sm">
              <AlertCircle className="w-8 h-8" />
            </div>
            <h2 className="text-xl font-display uppercase tracking-wide text-slate-800">
              Update <span className="text-[#548DC8]">Required</span>
            </h2>
            <p className="text-sm font-medium text-slate-500 mt-2">
              For security, please add a mobile number to your account.
            </p>
          </div>

          <div className="p-8 space-y-5 bg-white">
            {error && (
              <div className="bg-rose-50 text-rose-600 border border-rose-200 px-4 py-3 rounded-lg text-sm font-semibold">
                {error}
              </div>
            )}

            <div className="flex flex-col gap-2">
              <label className="text-xs font-bold text-slate-400 uppercase tracking-widest flex items-center gap-1.5">
                <Phone className="w-3 h-3" /> Phone Number
              </label>

              <PhoneInput
                placeholder="Enter phone number"
                value={phone}
                onChange={setPhone}
                international
                defaultCountry="LK"
                flags={flags}
              />
            </div>

            <button
              onClick={handleSave}
              disabled={isSaving || !phone}
              className="w-full bg-[#548DC8] hover:bg-[#4377B0] text-white py-3.5 px-6 rounded-xl font-bold uppercase tracking-wider text-sm flex items-center justify-center gap-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed drop-shadow-md mt-6"
            >
              {isSaving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" /> Save Number
                </>
              )}
            </button>
            <style jsx global>{`
              .PhoneInput {
                width: 100%;
                display: flex;
                align-items: center;
                background-color: #f8fafc;
                border: 1px solid #e2e8f0;
                border-radius: 0.75rem;
                padding: 0;
                transition: all 0.2s;
                min-height: 52px;
              }
              .PhoneInput:focus-within {
                border-color: #548dc8;
                box-shadow: 0 0 0 1px #548dc8;
              }
              .PhoneInputCountry {
                display: flex;
                align-items: center;
                padding: 0.8rem 1rem 0.8rem 1rem;
                border-right: 1px solid #e2e8f0;
                cursor: pointer;
              }
              .PhoneInputCountryIcon {
                margin-right: 8px;
                width: 24px;
                height: 16px;
              }
              .PhoneInputCountryIcon img {
                width: 24px;
                height: 16px;
              }
              .PhoneInputCountrySelect {
                background: transparent;
                border: none;
                padding: 0;
                margin: 0;
                cursor: pointer;
                outline: none;
                color: #334155;
                font-size: 1rem;
                font-weight: 500;
              }
              .PhoneInputCountrySelectArrow {
                margin-left: 6px;
                width: 0;
                height: 0;
                border-left: 5px solid transparent;
                border-right: 5px solid transparent;
                border-top: 6px solid #64748b;
              }
              .PhoneInputCountryCallingCode {
                margin-left: 6px;
                color: #64748b;
                font-size: 1rem;
                font-weight: 500;
              }
              .PhoneInputInput {
                flex: 1;
                background: transparent;
                border: none;
                padding: 0.8rem 1rem;
                font-size: 1rem;
                font-weight: 500;
                color: #334155;
                outline: none;
              }
              .PhoneInputInput::placeholder {
                color: #cbd5e1;
              }
            `}</style>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
